<script lang="ts">
	import { setContext } from "svelte";
	import Layout from "../components/Layout.svelte";
	import Link from "../components/Link.svelte";
	import BlogSection from "../components/BlogSection.svelte";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import HotlineGetInTouch from "../components/HotlineGetInTouch.svelte";
	import ContactForm from "../components/Contact/ContactForm.svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import { getTenant } from "../getTenant.js";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { contactPathMap } from "../../../core/schema/paths/contactPathMap.js";
	import { caseStudiesPathMap } from "../../../core/schema/paths/caseStudiesPathMap.js";
	import { referenceToCard } from "../components/referenceToCard.js";
	import arrowRight from "../../../core/assets/icons/arrow-right.svg?raw";
	import translations from "./ServiceSupportDetail.translations.json";
	import type { ServiceSupportDetailProps } from "./ServiceSupportDetailProps.js";
	import heroImage from "../assets/images/integration/integration1.png";
	import heroImageSm from "../assets/images/integration/integration1.png?webp&h=420&w=608&imagetools";
	import heroImageMd from "../assets/images/integration/integration1.png?webp&h=520&w=704&imagetools";
	import heroImageLg from "../assets/images/integration/integration1.png?webp&h=560&w=960&imagetools";
	import heroImageXl from "../assets/images/integration/integration1.png?webp&h=640&w=1424&imagetools";
	import inlineImageSm from "../assets/images/integration/integration2.png?webp&h=320&w=608&imagetools";
	import inlineImageMd from "../assets/images/integration/integration2.png?webp&h=360&w=704&imagetools";
	import inlineImageLg from "../assets/images/integration/integration2.png?webp&h=400&w=800&imagetools";
	import wideImageSm from "../assets/images/integration/integration3.png?webp&h=320&w=768&imagetools";
	import wideImageMd from "../assets/images/integration/integration3.png?webp&h=380&w=1024&imagetools";
	import wideImageLg from "../assets/images/integration/integration3.png?webp&h=460&w=1440&imagetools";
	import wideImageXl from "../assets/images/integration/integration3.png?webp&h=560&w=1920&imagetools";

	type $$Props = ServiceSupportDetailProps;

	const tenant = getTenant();
	const { relatedCaseStudies } = $$restProps as $$Props;
	const translate = getTranslate(translations);

	setContext(thumbnailImageContextKey, heroImage);

	const services = [
		{ label: translate("service1Label"), text: translate("service1Text") },
		{ label: translate("service2Label"), text: translate("service2Text") },
		{ label: translate("service3Label"), text: translate("service3Text") },
		{ label: translate("service4Label"), text: translate("service4Text") },
		{ label: translate("service5Label"), text: translate("service5Text") },
	];
</script>

<Layout>
	<article>
		<section class="!mb-0">
			<div class="border-lttertiary border-b">
				<div
					class="mx-auto max-w-[120rem] px-4 pb-12 pt-6 sm:px-8 md:px-16 md:pb-20 md:pt-10 lg:pb-24 lg:pt-12 xl:px-32"
				>
					<div
						class="text-ltprimary-200 mb-8 flex items-center gap-4 text-xs uppercase tracking-[0.15em] md:mb-12 lg:mb-16"
					>
						<span class="bg-ltprimary-200 h-px w-8"></span>
						<span>{translate("tag")}</span>
					</div>

					<div class="grid grid-cols-1 gap-6 md:gap-10 lg:grid-cols-2 lg:gap-16 xl:gap-32">
						<h1
							class="text-ltprimary sm:text-3lg m-0 p-0 text-[2.25rem]/[1.1] font-light uppercase tracking-[0.02em] md:text-2xl lg:text-2xl xl:text-3xl"
						>
							{nbspify(translate("headline"))}
						</h1>
						<div class="text-ltsecondary flex max-w-[52rem] flex-col gap-6 md:gap-8">
							{#each translate("introParagraph").split("\n") as introParagraph}
								<p class="text-sm leading-[1.6] tracking-[0.01em] md:text-base">
									{nbspify(introParagraph)}
								</p>
							{/each}
							<div>
								<Link asButton variant="primary" icon={arrowRight} href="/{contactPathMap[tenant.locale]}">
									{translate("contactUs")}
								</Link>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>

		<section class="container !mb-0">
			<div class="mx-auto flex max-w-5xl flex-col gap-8 px-4 pb-16 pt-8 sm:px-8">
				<h2
					class="text-ltprimary font-roboto text-2lg/[1.1] m-0 font-light uppercase tracking-[0.02em] md:text-[2.5rem]/none"
				>
					{translate("section1Heading")}
				</h2>
				<p class="text-ltsecondary lg:text-md text-sm leading-[1.6] tracking-[0.01em] md:text-base/normal">
					{nbspify(translate("section1Paragraph1"))}
				</p>

				<StaticPicture
					image={heroImageXl}
					alt="{translate('headline')} - {translate('ilustration')}"
					width={1424}
					height={640}
					loading="lazy"
					imgClass="w-full object-cover rounded-lg"
					class="w-full"
					sourcesets={{
						"<md": heroImageSm,
						">md": heroImageMd,
						">lg": heroImageLg,
						">xl": heroImageXl,
					}}
				/>

				<p class="text-ltsecondary lg:text-md text-sm leading-[1.6] tracking-[0.01em] md:text-base/normal">
					{nbspify(translate("section1Paragraph2"))}
				</p>

				<StaticPicture
					image={inlineImageLg}
					alt="{translate('headline')} - {translate('ilustration')}"
					width={800}
					height={400}
					loading="lazy"
					imgClass="w-full object-cover rounded-lg"
					class="w-full"
					sourcesets={{
						"<md": inlineImageSm,
						">md": inlineImageMd,
						">lg": inlineImageLg,
						">xl": inlineImageLg,
					}}
				/>

				<h2
					class="text-ltprimary font-roboto text-2lg/[1.1] m-0 mt-4 font-light uppercase tracking-[0.02em] md:text-[2.5rem]/none"
				>
					{nbspify(translate("section2Heading"))}
				</h2>

				<div class="bg-lttertiary-100 flex flex-col overflow-hidden rounded-md">
					{#each services as service, index}
						<div
							class="grid grid-cols-1 gap-3 p-5 sm:p-6 md:grid-cols-[minmax(0,14rem)_1fr] md:gap-8 md:p-8 {index <
							services.length - 1
								? 'border-b-lttertiary border-b'
								: ''}"
						>
							<div
								class="text-ltprimary-400 font-roboto text-md/tighter md:text-2md/none font-normal uppercase tracking-[0.02em]"
							>
								{service.label}
							</div>
							<div class="text-ltsecondary text-sm leading-[1.6] md:text-base/normal">
								{nbspify(service.text)}
							</div>
						</div>
					{/each}
				</div>
			</div>
		</section>

		<div class="mx-auto w-full max-w-[120rem]">
			<StaticPicture
				image={wideImageXl}
				alt="{translate('headline')} - {translate('ilustration')}"
				width={1920}
				height={560}
				loading="lazy"
				imgClass="h-[13.75rem] w-full object-cover sm:h-80 md:h-[25rem] lg:h-[30rem] xl:h-[35rem]"
				class="w-full"
				sourcesets={{
					"<md": wideImageSm,
					">md": wideImageMd,
					">lg": wideImageLg,
					">xl": wideImageXl,
				}}
			/>
		</div>

		<HotlineGetInTouch variant="bordered" />

		{#if relatedCaseStudies.length > 0}
			<section>
				<div class="border-lttertiary border-b">
					<div class="mx-4 my-10 md:mx-12 xl:mx-32">
						<h2
							class="text-ltprimary text-2lg/none m-0 text-center font-light uppercase tracking-[0.02em] md:text-[2.5rem]"
						>
							{translate("caseStudiesHeadline")}
						</h2>
					</div>
					<BlogSection cards={relatedCaseStudies.map(referenceToCard)} />
					<Link
						asButton
						variant="secondary"
						class="mx-auto my-8 md:my-10"
						icon={arrowRight}
						href="/{caseStudiesPathMap[tenant.locale]}"
					>
						{translate("caseStudiesButton")}
					</Link>
				</div>
			</section>
		{/if}

		<section class="!mb-0">
			<ContactForm containerClass="mx-4 md:px-8 lg:px-12 xl:px-28" />
		</section>
	</article>
</Layout>
